/**
 * CALLBACK.JSX v3.0 - LOGTO CALLBACK PAGE
 *
 * v3.0: FIX fuer haengenden Callback auf STRATO
 *   - Verwendet useLogto().isAuthenticated als primaeres Signal
 *   - useHandleSignInCallback nur fuer Token-Exchange, NICHT fuer Redirect
 *   - Fallback-Timeout nach 10 Sekunden
 *   - Umfangreiches Error-Logging
 *
 * v2.0: Web + Native Support
 */

import React, { useEffect, useState, useRef } from 'react';
import { useHandleSignInCallback, useLogto } from '@logto/react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';

const isNative = Capacitor.isNativePlatform();

function Callback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const hasRedirected = useRef(false);

  // ✅ v3.0: isAuthenticated als primaeres Redirect-Signal
  const { isAuthenticated } = useLogto();

  // Debug: Callback URL analysieren
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    console.log('[Callback] URL:', window.location.href);
    console.log('[Callback] Params:', Object.fromEntries(params.entries()));
    console.log('[Callback] Platform:', isNative ? 'Native' : 'Web');

    // Session-Storage Debug
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      keys.push(sessionStorage.key(i));
    }
    console.log('[Callback] SessionStorage keys:', keys);

    if (params.get('error')) {
      console.error('[Callback] OAuth Error:', params.get('error'), params.get('error_description'));
    }
  }, []);

  // ✅ v3.0: Token-Exchange via useHandleSignInCallback
  // WICHTIG: Kein Redirect in der Callback-Funktion!
  // Der Redirect passiert ueber isAuthenticated (unten)
  const { isLoading, error } = useHandleSignInCallback(() => {
    console.log('[Callback] useHandleSignInCallback success callback fired');
  });

  // ✅ v3.0: Redirect wenn authentifiziert (zuverlaessiger als Hook-Callback)
  useEffect(() => {
    if (isAuthenticated && !hasRedirected.current) {
      hasRedirected.current = true;
      console.log('[Callback] isAuthenticated=true, navigating to /');
      setStatus('success');
      // Kurze Verzoegerung damit der User "Erfolgreich" sieht
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 300);
    }
  }, [isAuthenticated, navigate]);

  // ✅ v3.0: Error-Handling
  useEffect(() => {
    if (error && !hasRedirected.current) {
      console.error('[Callback] Token exchange error:', error);
      console.error('[Callback] Error details:', {
        message: error.message,
        name: error.name,
        code: error.code
      });
      setStatus('error');

      // Bei Fehler: Zur Startseite nach 3 Sekunden
      setTimeout(() => {
        if (!hasRedirected.current) {
          hasRedirected.current = true;
          navigate('/', { replace: true });
        }
      }, 3000);
    }
  }, [error, navigate]);

  // ✅ v3.0: Fallback-Timeout - wenn nach 10s nichts passiert
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!hasRedirected.current && !isAuthenticated) {
        console.warn('[Callback] TIMEOUT nach 10s - erzwinge Redirect');
        console.warn('[Callback] State: isLoading=', isLoading, 'isAuthenticated=', isAuthenticated, 'error=', error);
        hasRedirected.current = true;
        // Harter Redirect statt navigate (falls React Router Probleme hat)
        window.location.replace('/');
      }
    }, 10000);
    return () => clearTimeout(timeout);
  }, [isLoading, isAuthenticated, error]);

  // ✅ v2.0: Native Callback Handling (unveraendert)
  useEffect(() => {
    if (isNative) {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const nativeError = urlParams.get('error');

      if (nativeError) {
        setStatus('error');
        setTimeout(() => navigate('/', { replace: true }), 2000);
        return;
      }

      if (code) {
        setStatus('success');
        setTimeout(() => { window.location.href = '/'; }, 1000);
      } else {
        setStatus('waiting');
        setTimeout(() => navigate('/', { replace: true }), 2000);
      }
    }
  }, [navigate]);

  const getStatusMessage = () => {
    if (isNative) {
      switch (status) {
        case 'success': return { text: 'Anmeldung erfolgreich!', subtext: 'Sie werden weitergeleitet...' };
        case 'error': return { text: 'Anmeldung fehlgeschlagen', subtext: 'Zurueck zur Startseite...' };
        case 'waiting': return { text: 'Warte auf Anmeldung...', subtext: 'Bitte schliessen Sie den Login ab.' };
        default: return { text: 'Verarbeite Anmeldung...', subtext: 'Bitte warten...' };
      }
    }
    // Web
    if (error) {
      return { text: 'Fehler beim Anmelden', subtext: error.message || 'Unbekannter Fehler' };
    }
    if (isAuthenticated || status === 'success') {
      return { text: 'Erfolgreich!', subtext: 'Sie werden weitergeleitet...' };
    }
    return { text: 'Anmeldung wird verarbeitet...', subtext: 'Bitte warten Sie einen Moment.' };
  };

  const showSpinner = isLoading || status === 'processing' || status === 'waiting';
  const statusInfo = getStatusMessage();

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
      color: 'white',
      padding: '20px'
    }}>
      {showSpinner && (
        <div style={{
          width: '60px',
          height: '60px',
          border: '6px solid rgba(255,255,255,0.3)',
          borderTop: '6px solid white',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
      )}

      {!showSpinner && (
        <div style={{ fontSize: '60px', marginBottom: '10px' }}>
          {error ? '\u274C' : '\u2705'}
        </div>
      )}

      <p style={{ marginTop: '24px', fontSize: '18px', fontWeight: 600, textAlign: 'center' }}>
        {statusInfo.text}
      </p>
      <p style={{ marginTop: '8px', fontSize: '14px', opacity: 0.8, textAlign: 'center', maxWidth: '400px' }}>
        {statusInfo.subtext}
      </p>

      <p style={{ marginTop: '30px', fontSize: '11px', opacity: 0.5 }}>
        {isNative ? 'Native App' : 'Web App'}
      </p>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default Callback;
